/** Typed-text widgets of the record editor: text / email, textarea (autosize, 12 lines
 *  then scroll), lazy text (read in place, a textarea on click), integer and number (a
 *  decimal: a catalog record's price). All outlined `size="small"`, labelled by the
 *  FieldSlot's label row (`id`), described by its caption (`describedBy`). (A person's phone
 *  numbers are rows of the contacts list, `ContactsWidget`.)
 *
 *  Integer and number keep the operator's text while they type: the draft holds the wire
 *  value (a number), and an input that is not a value yet ("4x") is reported through
 *  `onInvalid` instead of reaching the draft. A bounded integer (`boundedIntegerWidget`: a
 *  field whose catalog names no bound the server enforces — an itinerary day's span) keeps a
 *  whole number its rule refuses out of the draft the same way, in the rule's words — so it
 *  never reaches the preview either.
 */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState, type Ref } from 'react';

import type { RecordWidget, RecordWidgetProps } from '../../editor/types';

function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

function common(p: RecordWidgetProps) {
  return {
    id: p.id,
    size: 'small' as const,
    fullWidth: true,
    error: p.error,
    disabled: p.disabled,
    onFocus: p.onFocus,
    onBlur: p.onBlur,
  };
}

export function TextWidget(p: RecordWidgetProps) {
  const { field } = p;
  return (
    <TextField
      {...common(p)}
      type={field.widget === 'email' ? 'email' : 'text'}
      value={asText(p.value)}
      onChange={(e) => p.onChange(e.target.value)}
      inputProps={{
        'aria-describedby': p.describedBy,
        ...(field.max_length ? { maxLength: field.max_length } : {}),
        autoComplete: 'off',
        spellCheck: field.widget === 'email' ? false : undefined,
      }}
      InputProps={p.endAdornment ? { endAdornment: p.endAdornment } : undefined}
    />
  );
}

export function TextareaWidget(p: RecordWidgetProps & { inputRef?: Ref<HTMLTextAreaElement> }) {
  const { field } = p;
  return (
    <TextField
      {...common(p)}
      {...(p.inputRef ? { inputRef: p.inputRef } : {})}
      multiline
      // Grows with the text like the view-mode value it replaces (no re-layout on Edit).
      minRows={1}
      maxRows={12}
      value={asText(p.value)}
      onChange={(e) => p.onChange(e.target.value)}
      inputProps={{
        'aria-describedby': p.describedBy,
        ...(field.max_length ? { maxLength: field.max_length } : {}),
      }}
    />
  );
}

/** Long text that is read far more often than edited (a tour's twenty content texts):
 *  in edit mode it keeps showing the text as view mode does — the same box, so pressing
 *  E moves nothing and twenty inputs are not mounted at once — and becomes a textarea
 *  when clicked or focused (Enter / Space), focused at the end of the text. Once open it
 *  stays open while the slot is mounted. */
export function TextLazyWidget(p: RecordWidgetProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!open || !ref.current) return;
    const el = ref.current;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange?.(end, end);
  }, [open]);
  if (open) return <TextareaWidget {...p} inputRef={ref} />;
  const text = asText(p.value);
  return (
    <Box
      id={p.id}
      role="button"
      tabIndex={p.disabled ? -1 : 0}
      aria-describedby={p.describedBy}
      aria-label={`Edit ${p.field.label}`}
      aria-disabled={p.disabled || undefined}
      data-text-lazy
      onClick={() => !p.disabled && setOpen(true)}
      onKeyDown={(e) => {
        if (p.disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
        }
      }}
      sx={{
        position: 'relative',
        flex: '1 1 auto',
        minWidth: 0,
        cursor: p.disabled ? 'default' : 'text',
        borderRadius: 1,
        // An outline, not a border: the box keeps view mode's geometry.
        outline: '1px dashed transparent',
        '&:hover': { outlineColor: (t) => t.palette.divider },
        '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}` },
      }}
    >
      <Typography
        variant="body2"
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          maxHeight: '18em',
          overflowY: 'auto',
          overflowWrap: 'anywhere',
          color: text ? 'text.primary' : 'text.secondary',
          pr: 3,
        }}
      >
        {text || 'Empty — click to write'}
      </Typography>
      <EditOutlinedIcon aria-hidden sx={{ position: 'absolute', top: 2, right: 2, fontSize: 16, color: 'text.secondary' }} />
    </Box>
  );
}

/** Holds the typed text while the field has focus (or while it is not a value yet), and
 *  shows the draft's value otherwise — an undo from the ledger then shows through and
 *  drops what the input could not hold. */
function useTypedText(value: unknown, onInvalid: (message: string | null) => void) {
  const [text, setText] = useState<string | null>(null);
  const focused = useRef(false);
  const shown = text ?? asText(value);
  const lastValue = useRef(value);
  useEffect(() => {
    if (lastValue.current === value) return;
    lastValue.current = value;
    if (!focused.current) {
      setText(null);
      onInvalid(null);
    }
  }, [value, onInvalid]);
  return { shown, text, setText, focused };
}

const INTEGER_RE = /^-?\d+$/;

/** An integer input; `refuse` (a bounded field's rule: why a whole number — or a blank, null
 *  — cannot be its value, or null) keeps what it refuses out of the draft as it keeps "4x" out,
 *  in its words, and the typed text stays on blur so the error names what is shown. */
function integerInput(refuse?: (value: number | null) => string | null) {
  /** A typed text the draft takes (trimmed): blank or a whole number the rule does not refuse. */
  const takes = (t: string) =>
    (t === '' || INTEGER_RE.test(t)) && !refuse?.(t === '' ? null : Number.parseInt(t, 10));
  return function IntegerInput(p: RecordWidgetProps) {
    const { onInvalid } = p;
    const { shown, text, setText, focused } = useTypedText(p.value, onInvalid);
    return (
      <TextField
        {...common(p)}
        value={shown}
        onFocus={() => {
          focused.current = true;
          p.onFocus();
        }}
        onBlur={() => {
          focused.current = false;
          // Keep text the draft could not take, so its error still names what is shown.
          if (text === null || takes(text.trim())) setText(null);
          p.onBlur();
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const t = raw.trim();
          if (t !== '' && !INTEGER_RE.test(t)) {
            onInvalid('Enter a whole number');
            return;
          }
          const value = t === '' ? null : Number.parseInt(t, 10);
          const refused = refuse?.(value) ?? null;
          if (refused) {
            onInvalid(refused);
            return;
          }
          onInvalid(null);
          p.onChange(value);
        }}
        inputProps={{ 'aria-describedby': p.describedBy, inputMode: 'numeric', autoComplete: 'off' }}
        sx={{ maxWidth: 200 }}
      />
    );
  };
}

export const IntegerWidget = integerInput();

/** An integer field with a bound its catalog does not name (`refuse`: why a value cannot be
 *  saved — a whole number, or null for a blank — or null): the input keeps a refused value out
 *  of the draft (`integerInput`), and a refused value the draft holds anyway (a restored copy)
 *  is the same error. Its values read as the integer they are. */
export function boundedIntegerWidget(refuse: (value: number | null) => string | null): RecordWidget {
  return Object.assign(integerInput(refuse), {
    formatChange: (v: unknown) => String(v),
    checkValue: (to: unknown) =>
      to === null || to === undefined || to === ''
        ? refuse(null)
        : typeof to === 'number' && Number.isInteger(to)
          ? refuse(to)
          : 'Enter a whole number',
  });
}

/** A decimal as typed: digits with an optional fraction ("10", "10.5", "10.", ".5"). */
const DECIMAL_RE = /^-?(\d+\.?\d*|\.\d+)$/;

function DecimalInput(p: RecordWidgetProps) {
  const { onInvalid } = p;
  const { shown, text, setText, focused } = useTypedText(p.value, onInvalid);
  return (
    <TextField
      {...common(p)}
      value={shown}
      onFocus={() => {
        focused.current = true;
        p.onFocus();
      }}
      onBlur={() => {
        focused.current = false;
        if (text === null || text.trim() === '' || DECIMAL_RE.test(text.trim())) setText(null);
        p.onBlur();
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const t = raw.trim();
        if (t === '') {
          onInvalid(null);
          p.onChange(null);
        } else if (DECIMAL_RE.test(t)) {
          onInvalid(null);
          p.onChange(Number(t));
        } else {
          onInvalid('Enter a number');
        }
      }}
      inputProps={{ 'aria-describedby': p.describedBy, inputMode: 'decimal', autoComplete: 'off' }}
      sx={{ maxWidth: 200 }}
    />
  );
}

/** A decimal number (`number`): its value reads with at most two decimals ("12.5"). */
export const DecimalWidget: RecordWidget = Object.assign(DecimalInput, {
  formatChange: (v: unknown) =>
    typeof v === 'number' ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(v),
});
